import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import Locations from "../../../../../../components/Generic/Locations";
import SubHeader from "../../../../../../components/Generic/SubHeader";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { CustomerProvider } from "../../../../../../context/CustomerContext";
import { useAppState } from "../../../../../../context/StateContext";
import useActionAutomaticSequence from "../../../../../../hooks/useActionAutomaticSequence";
import { useCustomerHook } from "../../../../../../hooks/useCustomerHook";
import { AutomaticSequencing } from "../../../../../../models/automatic.sequencing";
import { Customer } from "../../../../../../models/customer";
import { Error } from "../../../../../../models/error";
import CustomerService from "../../../../../../services/customer.service";
import { checkError } from "../../../../../../util/checkError";
import {
  CompanyProfile,
  AutomaticSequenceOptions,
  CustomerType,
  CalledFrom,
} from "../../../../../../util/enums";
import { CustomerKeys } from "../../../../../../util/queries";
import SkeletonCustomer from "../../SkeletonDetails";
import { NetworkTabs } from "../../utils";
import Details from "./Details";
import Order from "./Order";
import ProjectsPage from "./Projects";
import Quote from "./Quote";
import useStyles from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";

// eslint-disable-next-line @typescript-eslint/naming-convention
enum TAB_SELECTED {
  DETAILS = "details",
  LOCATIONS = "locations",
  QUOTES = "quotes",
  ORDERS = "orders",
}

interface LocationProps {
  viewOnly: true;
}

interface Props {
  customer?: Customer;
  modalMode?: boolean;
  open?: boolean;
  onClose?: (customer?: Customer) => void;
  isDelivery?: boolean;
}

const CustomerDetails = (props: Props) => {
  const { customer, modalMode, open, onClose, isDelivery = false } = props;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { customer: _customer, setCustomer } = useCustomerHook();
  const queryClient = useQueryClient();

  const { state } = useLocation<LocationProps>();

  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(TAB_SELECTED.DETAILS);

  const [viewOnly, setViewOnly] = useState<boolean>();
  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [responseError, setResponseError] = useState<Error>();

  const { getAutomaticSequenceByType, isAutomaticSequenceReference, increaseAutomaticSequence } =
    useActionAutomaticSequence();

  getAutomaticSequenceByType(AutomaticSequenceOptions.CUSTOMER, !_customer?.id, setSequence);

  const { currentUser, isFMorSPVersion, userPermissions } = useAppState();

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const history = useHistory<any>();

  const locationState = history.location?.state;

  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [customerPermission, setCustomerPermission] = useState<{
    edit: boolean,
    create: boolean
  }>({
    edit: false,
    create: false
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setCustomerPermission({
        edit: checkResourceCan(userPermissions, ResourceName.CUSTOMERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_EDIT),
        create: checkResourceCan(userPermissions, ResourceName.CUSTOMERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_ADD_NEW),
      })
    }
  }, [userPermissions])

  useEffect(() => {
    if (modalMode && open && customer) {
      let mobilePhone = customer?.phone;

      if (mobilePhone && !mobilePhone.includes("+") && isFMorSPVersion)
        mobilePhone = `+${customer?.phone}`;
      setCustomer({ ...customer, phone: mobilePhone });
    }
  }, [open, customer, modalMode]);

  useEffect(() => {
    setCustomerId(locationState?.customerId || _customer?.id);
  }, [locationState, _customer]);

  useEffect(() => {
    if (state) setViewOnly(state.viewOnly);
  }, [state]);

  const { isFetching } = useQuery({
    queryKey: CustomerKeys.getById(customerId),
    queryFn: () =>
      CustomerService.getById(Number(customerId), {
        include: JSON.stringify([
          { association: "contacts", order: [["id", "DESC"]] },
          { association: "locations", order: [["id", "DESC"]] },
          { association: "timezone" },
          { association: "consultant" },
          { association: "warehouse" },
        ]),
      }),
    enabled: !!customerId && !modalMode,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) {
        let mobilePhone = response.data.phone;

        if (mobilePhone && !mobilePhone.includes("+") && isFMorSPVersion) {
          if (mobilePhone.includes(":")) {
            mobilePhone = `+${mobilePhone.split(": ")[1]}`;
          } else mobilePhone = `+${response.data.phone}`;
        }

        setCustomer({ ...response.data, phone: mobilePhone });
      }
    },
  });

  const { mutate: createCustomer, isLoading: isLoadingCreate } = useMutation({
    mutationKey: CustomerKeys.create(),
    mutationFn: (customer: Customer) => CustomerService.create(customer),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setResponseError(undefined);
      if (sequence && _customer?.code && sequence.id && isAutomaticSequenceReference(sequence, _customer.code)) {
        increaseAutomaticSequence({
          id: sequence.id,
          sequence,
          value: _customer.code,
        });
      }
      goBack();
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) {
        checkError(error, translate("anErrorOccurredTryAgain"));
        setResponseError(error.response.data);
      }
    },
  });

  const { mutate: updateCustomer, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: CustomerKeys.update(),
    mutationFn: ({ id, customer }: { id: number; customer: Customer }) =>
      CustomerService.update(id, customer),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setResponseError(undefined);
      goBack();

      queryClient.invalidateQueries({ queryKey: [CustomerKeys.all] });
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) {
        checkError(error, translate("anErrorOccurredTryAgain"));
        setResponseError(error.response.data);
      }
    },
  });

  const getPrimaryContact = () => {
    if (_customer.type !== CustomerType.PRIVATE) return _customer.contacts;
    const mobilePhone =
      isDelivery && _customer.destinationMobile ? _customer.destinationMobile : _customer.phone;

    const primaryContact = {
      email: _customer.email || "",
      firstName: _customer.name || "",
      lastName: _customer.lastName || "",
      isPrimary: true,
      mobilePhone: mobilePhone || "",
    };

    if (_customer?.contacts) {
      const containsCustomerContact = _customer.contacts.find(
        (contact) => contact.customerId === _customer.id
      );

      if (containsCustomerContact) return _customer.contacts;

      const primaryContactIndex = _customer.contacts.findIndex(
        (contact) => contact.isPrimary && contact.customerId !== _customer.id
      );

      const contactsAux = [..._customer.contacts];

      if (primaryContactIndex !== -1) {
        contactsAux[primaryContactIndex] = primaryContact;

        return contactsAux;
      }
      contactsAux.push(primaryContact);

      return contactsAux;
    }

    return [primaryContact];
  };

  const goBack = () => {
    if (modalMode) {
      if (onClose) {
        setCustomerId(undefined);
        onClose(_customer);
      }
    } else if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace(`${PRIVATE_ROUTES.CUSTOMERS}`);
    }
  };

  const detailsPanel: PanelProps = {
    key: "details",
    className: classnames(classes.uiTab, classes.tabDetails),
    menuItem: translate("details"),
    loading: isFetching,
    skeleton: () => <SkeletonCustomer />,
    render: () => {
      if (!isShowAction(currentUser, customerPermission?.create)) return null;

      return (
        <Details
          goBack={goBack}
          isLoadingCreate={isLoadingCreate}
          isLoadingUpdate={isLoadingUpdate}
          viewOnly={viewOnly}
          onDelete={() =>
            _customer && _customer?.id
              ? updateCustomer({
                  id: _customer?.id,
                  customer: { ..._customer, valid: false },
                })
              : null
          }
          onCreate={() =>
            createCustomer({
              ..._customer,
              contacts: getPrimaryContact() || [],
            })
          }
          onUpdate={(customerUpdated) =>
            _customer && _customer?.id
              ? updateCustomer({
                  id: _customer?.id,
                  customer: customerUpdated || {
                    ..._customer,
                    contacts: getPrimaryContact() || [],
                  },
                })
              : null
          }
          sequence={sequence}
          responseError={responseError}
        />
      )
    },
  };

  const locationsPanel: PanelProps = {
    key: "locations",
    className: classes.uiTab,
    menuItem: translate("locations"),
    render: () => (
      <Locations
        loading={isFetching}
        viewOnly={viewOnly}
        locations={_customer?.locations}
        onChange={(locations) => setCustomer({ ..._customer, locations })}
        calledFrom={CalledFrom.CUSTOMER}
      />
    ),
  };

  const projectsPanel: PanelProps = {
    key: "projects",
    className: classes.uiTab,
    menuItem: translate("projects"),
    render: () => <ProjectsPage customer={_customer} />,
  };

  const quotesPanel: PanelProps = {
    key: "quotes",
    className: classes.uiTab,
    menuItem: translate("quotes"),
    render: () => <Quote viewOnly={viewOnly} customerId={customerId} />,
  };

  const ordersPanel: PanelProps = {
    key: "orders",
    className: classes.uiTab,
    menuItem: translate("orders"),
    render: () => <Order viewOnly={viewOnly} customerId={customerId} />,
  };

  const getLeftPanels = () => {
    if (!customerId) return [detailsPanel];

    if (currentUser?.company?.profile?.initials === CompanyProfile.MV) {
      if (!customerPermission?.edit) return [];
      return [detailsPanel, locationsPanel, quotesPanel, ordersPanel];
    }

    return [detailsPanel, projectsPanel, locationsPanel, ordersPanel];
  };

  const getCustomerName = () => {
    if (_customer && _customer?.name && _customer?.lastName) {
      return `${_customer?.name} ${_customer?.lastName}`;
    }

    if (_customer && _customer?.name) {
      return _customer?.name;
    }

    return "";
  };

  const renderContent = () => {
    if (modalMode) {
      return open ? (
        <Details
          modalMode
          isDelivery={isDelivery}
          open={open}
          goBack={() => {
            setCustomerId(undefined);
            onClose?.();
          }}
          viewOnly={viewOnly}
          isLoadingCreate={isLoadingCreate}
          isLoadingUpdate={isLoadingUpdate}
          isLoadingCustomer={isFetching}
          onDelete={() =>
            _customer && _customer?.id
              ? updateCustomer({
                  id: _customer?.id,
                  customer: { ..._customer, valid: false },
                })
              : null
          }
          onCreate={() => createCustomer(_customer)}
          onUpdate={(customerUpdated) =>
            _customer && _customer?.id
              ? updateCustomer({
                  id: _customer?.id,
                  customer: customerUpdated || _customer,
                })
              : null
          }
        />
      ) : null;
    }

    return (
      <div>
        {!isMoving(currentUser?.company) && (
          <SubHeader>
            <Breadcrumb
              items={[
                {
                  title: translate("network"),
                  link: {
                    url: PRIVATE_ROUTES.NETWORK,
                  },
                },
                {
                  title: translate("customers"),
                  link: {
                    url: `${PRIVATE_ROUTES.NETWORK}?tab=customers`,
                  },
                },
                {
                  title: getCustomerName(),
                  color: "#465869",
                },
              ]}
            />
          </SubHeader>
        )}

        <div className={classes.mainDiv}>
          <UITabMaterial
            useInitialPadding
            leftPanels={getLeftPanels()}
            showRightPanel={leftPanelSelected === TAB_SELECTED.DETAILS}
            onChangeLeftPanel={(event, panel, key) => key && setLeftPanelSelected(key)}
          />
        </div>
      </div>
    );
  };

  return renderContent();
};

const CustomerMaintenance = (props: Props) => {
  return (
    <CustomerProvider>
      <CustomerDetails {...props} />
    </CustomerProvider>
  );
};

export default CustomerMaintenance;
